import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { routes } from '@/lib/routes';
import { portalApi } from '../api';
import type { PublicPageOut } from '../types';

const CHANNELS = [
  {
    id: 'general',
    title: 'Общие обращения',
    fallback: 'Вопросы о платформе, предложения, обратная связь и общие организационные обращения.',
    cta: 'Создать обращение',
  },
  {
    id: 'academic',
    title: 'Академическое сотрудничество',
    fallback: 'Партнёрства, совместные проекты, научные мероприятия и институциональное взаимодействие.',
    cta: 'Написать',
  },
  {
    id: 'technical',
    title: 'Техническая поддержка',
    fallback: 'Ошибки интерфейса, вход, доступ, файлы и технические вопросы по работе системы.',
    cta: 'Открыть форму',
    aliases: ['support'],
  },
] as const;

export const ContactsSection = ({ focusId }: { focusId?: string }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [page, setPage] = useState<PublicPageOut | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi
      .getPage('contacts')
      .then((data) => setPage({ slug: 'contacts', content_blocks: data.content_blocks || [] }))
      .catch(() => setPage(null))
      .finally(() => setLoading(false));
  }, []);

  const resolvedFocus = focusId === 'support' ? 'technical' : focusId;

  useEffect(() => {
    if (!resolvedFocus || loading) return;
    document.getElementById(`portal-${resolvedFocus}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [resolvedFocus, loading, page]);

  if (loading) {
    return <div className="public-empty">Загрузка...</div>;
  }

  const blocks = page?.content_blocks || [];
  const blocksMap = new Map(blocks.map((b) => [b.block_type, b.content]));

  const textFor = (keys: string[], fallback: string) => {
    for (const key of keys) {
      const value = blocksMap.get(key)?.text;
      if (typeof value === 'string' && value.trim()) return value;
    }
    return fallback;
  };

  const openContact = () => {
    navigate(user ? routes.newRequest : routes.login);
  };

  return (
    <div className="portal-section">
      <div className="public-hero">
        <div>
          <span className="public-eyebrow">Связь</span>
          <h2>Контакты</h2>
          <p>Публичные каналы обращений, которые владелец разрешил показывать на портале.</p>
        </div>
      </div>

      <section className="portal-contact-grid">
        {CHANNELS.map((channel, index) => (
          <article key={channel.id} id={`portal-${channel.id}`}>
            <span>0{index + 1}</span>
            <h2>{channel.title}</h2>
            <p>
              {textFor(
                [channel.id, ...('aliases' in channel ? channel.aliases : [])],
                channel.fallback,
              )}
            </p>
            <button type="button" onClick={openContact}>
              {channel.cta} →
            </button>
          </article>
        ))}
      </section>

      <section className="portal-contact-note">
        <strong>Публичные данные</strong>
        <p>
          На этой странице показываются только контактные каналы, которые владелец разрешил публиковать.
          Личные, банковские и закрытые account-поля автоматически не выводятся.
        </p>
      </section>
    </div>
  );
};
